import { Fragment, useMemo, useState } from 'react'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { ChartFrame } from '@/shared/ui/charts'
import { MultiSelect } from '@/shared/ui/filters'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import type {
  SurveyGroupDto, SurveyQuestionDto, TrackerTrendDto, TrackerTrendPointDto, TrackerTrendWaveDto,
} from '@/shared/api/types'
import { getInstrument, getTrackerTrend, listPolls } from './api'
import { Pill, baseStatement, count, countish, pct, pp } from './vocab'

/**
 * One question of a tracker, wave on wave — and the sentences where the
 * comparison is refused.
 *
 * ## Why this panel replaced a paragraph
 *
 * Until `GET /survey-groups/{id}/trend` shipped, this page carried a note saying
 * no endpoint returned wave-on-wave estimates and that drawing a line here would
 * mean subtracting two separately weighted polls on the client. That was true and
 * it is no longer: the read exists, every point comes out of `survey_estimate`,
 * and nothing below computes a share, a margin or a difference.
 *
 * ## The field that carries the meaning is `deltaPp: null`
 *
 * PollSphere's `momentum` endpoint split a poll's responses by collection order
 * and labelled the halves SURGING or COLLAPSING. What it was measuring was which
 * enumerator happened to work later in the week. The defence against that is not
 * a better formula — it is the server declining to publish a difference when the
 * two ends are not the same measurement, which is what a null `deltaPp` plus a
 * composed `comparabilityNote` is. So the note is rendered **verbatim and above
 * the chart**, not in a tooltip: a reader who has already read "+2pp" and moved
 * on has been told the wrong thing, and a hover cannot un-tell it.
 *
 * ## Why small multiples rather than one multi-colour line chart
 *
 * `shared/ui/charts.tsx` sets the rule for the whole app: encode with length and
 * position, never with hue, because the chart tokens are adjacent enough to fail
 * a CVD check against each other (chocolate against rust is ΔE 5.3 for normal
 * vision in dark mode). Four candidates on four hues would be four lines nobody
 * can reliably tell apart. One panel per option, sharing an x domain AND a y
 * domain, keeps every comparison the reader wants — level between options, shape
 * across waves — without spending the identity channel on colour. `ChartFrame`
 * comes from that file so the panel headings read as the same system; the mark
 * itself is drawn here because the file exports no interval mark, and adding one
 * is outside this brief's file set.
 *
 * ## Why the interval is drawn and the line is not drawn through a hole
 *
 * A 2pp move inside a ±3pp band is not a move, so every point carries its
 * `moe95` as a band rather than a footnote. And a wave with a
 * `suppressedSentence` — no poll tagged into it, or an estimate withheld — is a
 * dashed gap with the server's sentence underneath, never a segment joining the
 * waves either side of it. A line drawn straight through a hole asserts a
 * measurement that was never taken.
 */

/** The one data hue, matching `shared/ui/charts.tsx`. Colour carries no identity here. */
const HUE = 'var(--chart-1)'

/**
 * The English for `Comparability`. The server owns the code list — this is a
 * label map, and an unrecognised code renders as itself so a seventh verdict
 * arrives visibly rather than as the word "Unknown".
 *
 * It lives here rather than in `vocab.tsx` because `vocab.tsx` is outside this
 * brief's file set; it belongs there the next time that file is open.
 */
const COMPARABILITY_LABEL: Record<string, string> = {
  IDENTICAL: 'Identical instrument',
  EQUIVALENT: 'Equivalent instrument',
  WORDING_CHANGED: 'Wording changed',
  OPTIONS_CHANGED: 'Options changed',
  BASE_CHANGED: 'Base changed',
  WEIGHTING_CHANGED: 'Weighting changed',
  UNKNOWN: 'Comparability not established',
}
const comparabilityLabel = (c?: string | null) =>
  (!c ? '—' : (COMPARABILITY_LABEL[c] ?? c))

const asNumber = (v: string | number | null | undefined): number | null => {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * A change in percentage points, signed.
 *
 * There is no null branch that prints a zero: an absent delta is the whole point
 * of the endpoint, and its caller renders a reason instead of calling this.
 */
function deltaPp(value: string | number): string {
  const n = asNumber(value)
  if (n === null) return '—'
  // U+2212, not a hyphen: this sits beside tabular numerals.
  return `${n > 0 ? '+' : n < 0 ? '−' : '±'}${Math.abs(n).toFixed(1)}pp`
}

const waveName = (w: TrackerTrendWaveDto) => w.label ?? `Wave ${w.waveNo}`

/**
 * The panel, mounted under one tracker's waves.
 *
 * The tracker is passed whole rather than by id: the caller has already fetched
 * the detail, and a second read of it here would be the N+1 the list/detail split
 * on this page exists to avoid.
 */
export function TrendPanel({ group }: { group: SurveyGroupDto }) {
  const canAnalyse = useCan('survey:analyse')
  const [questionId, setQuestionId] = useState('')
  const [optionIds, setOptionIds] = useState<string[]>([])

  /**
   * The instrument, reached through a poll.
   *
   * There is no route from a wave to its questions: `SurveyWaveDto` carries an
   * id, a number, dates and a poll count, and the trend response names a
   * `surveyId` per wave but only once a question has already been chosen. So the
   * question list comes from the polls tagged into this tracker — `?groupId=` is
   * a served filter — and from the newest of them that has a published version.
   */
  const polls = useLoad(
    () => (canAnalyse ? listPolls({ groupId: group.id }, 0, 100) : Promise.resolve(null)),
    [group.id, canAnalyse])

  const versionId = useMemo(() => {
    const rows = (polls.data?.content ?? []).filter((p) => p.currentVersion)
    // Newest wave first: a tracker's current instrument is the one the latest
    // wave fielded, and an older wave's version would offer questions that have
    // since been dropped.
    rows.sort((a, b) => (b.waveNo ?? 0) - (a.waveNo ?? 0))
    return rows[0]?.currentVersion?.id ?? null
  }, [polls.data])

  const instrument = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])

  const questions: SurveyQuestionDto[] = instrument.data?.questions ?? []
  const question = questions.find((q) => q.id === questionId) ?? null

  const trend = useLoad(
    () => (questionId ? getTrackerTrend(group.id, questionId, optionIds) : Promise.resolve(null)),
    // Joined rather than passed as an array: `useLoad` compares deps by identity
    // and a fresh array every render would refetch forever.
    [group.id, questionId, optionIds.join(',')])

  if (!canAnalyse) {
    return (
      <Note tone="info">
        A trend is a read of published estimates, so it needs <code className="font-mono">
        survey:analyse</code> — the same permission the Results screen asks for. Declaring and
        ordering the waves above needs only <code className="font-mono">survey:read</code>, which
        is why they are visible and this is not.
      </Note>
    )
  }

  const optionChoices = (question?.options ?? []).map((o) => ({
    value: o.id, label: o.label || o.code,
  }))

  return (
    <div className="space-y-3 border-t border-border pt-3">
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1 min-w-0">
          <span className="text-erp-xs font-medium text-muted-foreground">Question</span>
          <select
            className="erp-input h-8 text-erp-sm min-w-[18rem]"
            value={questionId}
            onChange={(e) => { setQuestionId(e.target.value); setOptionIds([]) }}
          >
            <option value="">
              {instrument.loading || polls.loading ? 'Loading the instrument…'
                : questions.length === 0 ? 'No instrument to read questions from'
                  : 'Choose a question…'}
            </option>
            {/*
              Every question is offered, including the ones with no options. The
              trend is a share per option, so a RATING_SCALE or an OPEN_TEXT item
              has nothing to plot — and the server says exactly that, naming the
              question and its type. Filtering them out here would leave somebody
              wondering why TURNOUT is missing from a list of the tracker's own
              questions; rule 3 is to offer the control and let the server refuse.
            */}
            {questions.map((q) => (
              <option key={q.id} value={q.id}>{q.code} — {q.text}</option>
            ))}
          </select>
        </label>

        {question && optionChoices.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-erp-xs font-medium text-muted-foreground">Options</span>
            <MultiSelect
              label="Options"
              options={optionChoices}
              selected={optionIds}
              onChange={setOptionIds}
              emptyLabel="Every option"
            />
          </div>
        )}
      </div>

      {!polls.loading && !instrument.loading && questions.length === 0 && (
        <Note tone="info">
          No poll tagged into {group.code} carries a published instrument, so there is no list of
          this tracker's questions to offer. The trend read itself does not publish one either —
          its refusal for an unknown question names the codes in prose, which is a sentence for a
          person rather than a list a picker can be built from. Tag a poll into a wave and its
          questions appear here.
        </Note>
      )}

      <ErrorStrip message={polls.error ?? instrument.error ?? trend.error} />

      {/*
        Nothing below the strip once a read is refused. `useLoad` keeps the last
        good response, and the picker deliberately offers questions the server
        will refuse — a rating scale has no share to plot — so leaving the
        figures up would put the PREVIOUS question's chart, table and
        comparability sentence under the refused question's error.
      */}
      {trend.error ? null : questionId && trend.loading && !trend.data ? (
        <LoadingBlock label="Loading the trend…" />
      ) : trend.data ? (
        <TrendBody trend={trend.data} />
      ) : questionId ? null : questions.length > 0 ? (
        <Note tone="info">
          Pick a question. A trend is per question — there is no &ldquo;the tracker&rsquo;s
          numbers&rdquo;, because a tracker carries a whole instrument and one line drawn across
          all of it would be averaging answers to different questions.
        </Note>
      ) : null}
    </div>
  )
}

/** The chart, the sentences and the table, once a trend has actually arrived. */
function TrendBody({ trend }: { trend: TrackerTrendDto }) {
  const waves = trend.waves

  /**
   * Whether any delta was actually withheld — read off the points, never guessed
   * from the trend-level `comparability` code.
   *
   * Which codes suppress a difference is the endpoint's decision and it is made
   * per point: a `WORDING_CHANGED` trend can still publish wave 1 → 2 because
   * the change landed later, and an `UNKNOWN` one can publish nothing at all.
   * Re-deriving that rule here put a bold "No change is published" over a chart
   * drawing changes, and demoted the server's sentence to a grey aside on the
   * one trend where every delta was null. A point counts only if it carries an
   * estimate and has an earlier wave to compare against — otherwise the null is
   * a first appearance, not a refusal.
   */
  const withheld = useMemo(() => waves.some((w, i) => w.points.some((p) => (
    p.deltaPp === null
    && asNumber(p.estimate) !== null
    && waves.slice(0, i).some((prev) => prev.points.some((q) => (
      q.optionId === p.optionId && asNumber(q.estimate) !== null)))
  ))), [waves])

  /**
   * The series, in the order the server first mentions each option.
   *
   * Built from the points rather than from the instrument so a filtered request
   * draws exactly what came back — and so an option that was dropped from the
   * current questionnaire but still has estimates in earlier waves is not
   * silently missing from its own history.
   */
  const series = useMemo(() => {
    const seen = new Map<string, { optionId: string; label: string; code: string }>()
    for (const w of waves) {
      for (const p of w.points) {
        if (!seen.has(p.optionId)) {
          seen.set(p.optionId, { optionId: p.optionId, label: p.optionLabel, code: p.optionCode })
        }
      }
    }
    return [...seen.values()]
  }, [waves])

  /**
   * One y domain for every panel, taken from the ENDS of the intervals.
   *
   * Scaling each option to its own peak would make a 9% candidate's wobble look
   * like a 40% candidate's, which is the exact misreading small multiples are
   * used to avoid. Taking the domain from `estimate ± moe95` rather than from the
   * estimates keeps a band from being clipped at the top of its panel.
   */
  const domain = useMemo(() => {
    let lo = Infinity
    let hi = -Infinity
    for (const w of waves) {
      for (const p of w.points) {
        const est = asNumber(p.estimate)
        if (est === null) continue
        const moe = asNumber(p.moe95) ?? 0
        lo = Math.min(lo, est - moe)
        hi = Math.max(hi, est + moe)
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return null
    const pad = Math.max((hi - lo) * 0.12, 0.01)
    return { lo: Math.max(0, lo - pad), hi: Math.min(1, hi + pad) }
  }, [waves])

  const gaps = waves.filter((w) => w.suppressedSentence)

  /**
   * The base is per option AND per wave, so it is printed on the panel it
   * belongs to.
   *
   * One figure taken from `points[0]` was whichever option the server happened
   * to mention first: on a wave carrying 1,204 for two options and 18 for a
   * suppressed third, that prints one option's base as the whole chart's.
   * Newest wave that has a point for THIS option, because an option dropped
   * from the current questionnaire still has a base in the wave it last ran in.
   */
  const baseFor = (optionId: string): string | null => {
    for (let i = waves.length - 1; i >= 0; i -= 1) {
      const p = waves[i]!.points.find((q) => q.optionId === optionId)
      if (p) return `${waveName(waves[i]!)} — ${baseStatement(p.baseN, trend.baseCode)}`
    }
    return null
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-erp-md font-medium">{trend.questionCode}</span>
        <span className="text-erp-sm text-muted-foreground min-w-0">{trend.questionText}</span>
        <Pill tone={withheld
          ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
          : undefined}>
          {comparabilityLabel(trend.comparability)}
        </Pill>
      </div>

      {/*
        VERBATIM, ABOVE THE CHART, AND IN THE ONE ACCENT THIS PANEL SPENDS.
        The server composed this sentence with the two instruments in front of it;
        nothing here rewrites, truncates or summarises it. Amber rather than red
        because a withheld delta is not an error — it is the endpoint working.
        `states.tsx` has only info/good/bad tones and is outside this brief's file
        set, so the warning surface is written out here.
      */}
      {trend.comparabilityNote && (
        // The dark pairing is the house one from `programs/parts.tsx`: this is
        // the surface that must stay prominent, and amber-on-amber-50 with no
        // `dark:` variant is unreadable in the shell most of this app runs in.
        <div className={`px-3 py-2 rounded-md border text-erp-sm ${withheld
          ? 'bg-amber-50 border-amber-200 text-amber-800 '
            + 'dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200'
          : 'bg-muted/40 border-border text-muted-foreground'}`}>
          {withheld && (
            // Not "for this wave": suppression is per point, and the waves it
            // applies to are named in the table's change column, not here.
            <strong className="font-semibold">Some waves publish no change. </strong>
          )}
          {trend.comparabilityNote}
        </div>
      )}

      {series.length === 0 || domain === null ? (
        <Note tone="info">
          No wave of {trend.groupCode} has published an estimate for {trend.questionCode} yet, so
          there is nothing to plot. The waves and their sentences are below.
        </Note>
      ) : (
        <div className="space-y-3">
          {series.map((s) => (
            <ChartFrame
              key={s.optionId}
              title={s.label || s.code}
              description={[
                `${s.code} · share of the base at each wave, with its 95% margin`,
                baseFor(s.optionId),
              ].filter(Boolean).join(' · ')}
            >
              <SeriesPlot waves={waves} optionId={s.optionId} lo={domain.lo} hi={domain.hi}
                label={s.label || s.code} />
            </ChartFrame>
          ))}

          {/* One axis for every panel — they share the domain, so they share the ticks. */}
          <div className="flex text-erp-xs text-muted-foreground/80">
            {waves.map((w) => (
              <span key={w.waveId} className="flex-1 text-center truncate" title={waveName(w)}>
                {waveName(w)}
              </span>
            ))}
          </div>

          <p className="text-erp-xs text-muted-foreground">
            Base code {trend.baseCode}. Each wave was weighted on its own and each option
            within it carries its own base — the panel headings state the newest, and every
            one of them is in the table below.
          </p>
        </div>
      )}

      {/*
        The hole, in the server's words. Rendered under the chart rather than
        inside it because a gap on a chart is a question and this is the answer to
        it — and because a wave with no poll is a fact about the tracker, not
        about one option's line.
      */}
      {gaps.map((w) => (
        <Note key={w.waveId} tone="info">
          <strong className="font-medium">{waveName(w)}: </strong>{w.suppressedSentence}
        </Note>
      ))}

      <TrendTable trend={trend} series={series} />
    </div>
  )
}

const PLOT_H = 76
const PLOT_PAD = 14

/**
 * One option's line, with its interval at every wave and a hole where there is one.
 *
 * Drawn as an SVG whose x coordinates are PERCENTAGES and whose y coordinates are
 * pixels. That combination is what keeps it responsive without
 * `preserveAspectRatio="none"`, which stretches the x axis by whatever the panel
 * happens to be wide and turns a 2px stroke into a wedge — the same correction
 * `shared/ui/charts.tsx` records against its own first version.
 */
function SeriesPlot({ waves, optionId, lo, hi, label }: {
  waves: TrackerTrendWaveDto[]
  optionId: string
  lo: number
  hi: number
  label: string
}) {
  const n = waves.length
  const span = hi - lo || 1
  const x = (i: number) => ((i + 0.5) / n) * 100
  const y = (v: number) =>
    PLOT_PAD + (1 - (v - lo) / span) * (PLOT_H - 2 * PLOT_PAD)

  const plotted = waves.map((w) => {
    const point = w.points.find((p) => p.optionId === optionId) ?? null
    const est = asNumber(point?.estimate)
    return { wave: w, point, est }
  })

  const drawn = plotted.filter((p) => p.est !== null).length

  return (
    <svg
      width="100%"
      height={PLOT_H}
      role="img"
      aria-label={`${label}: ${drawn} of ${n} waves carry an estimate.`}
      className="block"
    >
      {plotted.map(({ wave, est }, i) => (
        // The hole, marked in the plot itself. A wave the reader can see nothing
        // at is otherwise indistinguishable from a wave that is not there.
        est === null ? (
          <line key={wave.waveId} x1={`${x(i)}%`} x2={`${x(i)}%`}
            y1={PLOT_PAD} y2={PLOT_H - PLOT_PAD}
            stroke="var(--border)" strokeWidth={1} strokeDasharray="2 3" />
        ) : null
      ))}

      {/*
        Segments join ADJACENT waves only, and only when both ends were measured.
        Nothing bridges a hole: the wave with no poll in it is the one place a
        chart is most tempted to invent a reading, and a straight line across it
        is a claim nobody made.
      */}
      {plotted.map(({ est }, i) => {
        const next = plotted[i + 1]
        if (est === null || !next || next.est === null) return null
        return (
          <line key={`seg-${i}`} x1={`${x(i)}%`} y1={y(est)} x2={`${x(i + 1)}%`} y2={y(next.est)}
            stroke={HUE} strokeWidth={2} strokeLinecap="round" />
        )
      })}

      {plotted.map(({ wave, point, est }, i) => {
        if (est === null || !point) return null
        const moe = asNumber(point.moe95)
        // Above the interval, not above the dot: a label 10px over the estimate
        // lands inside its own band whenever the margin is wide, which is exactly
        // when the reader most needs to see both.
        const top = moe === null ? y(est) : y(Math.min(hi, est + moe))
        return (
          <g key={wave.waveId}>
            {/* The interval, as a band rather than a whisker with caps: a 2pp move
                inside a ±3pp band is not a move, and the band is the only mark on
                this chart that says so at a glance. */}
            {moe !== null && (
              <line x1={`${x(i)}%`} x2={`${x(i)}%`}
                y1={y(Math.min(hi, est + moe))} y2={y(Math.max(lo, est - moe))}
                stroke={HUE} strokeWidth={9} strokeOpacity={0.22} />
            )}
            <circle cx={`${x(i)}%`} cy={y(est)} r={3} fill={HUE} />
            {/* The delta beside the point, where the server published one. Where it
                did not, nothing is drawn — the reason is the note above the chart
                and the cell in the table, both of which are sentences. */}
            {point.deltaPp !== null && (
              <text x={`${x(i)}%`} y={Math.max(9, top - 4)} textAnchor="middle"
                fontSize={9} fill="currentColor" className="text-muted-foreground tabular-nums">
                {deltaPp(point.deltaPp)}
              </text>
            )}
          </g>
        )
      })}
    </svg>
  )
}

/**
 * Every figure the chart draws, plus the provenance the chart cannot carry.
 *
 * A row per wave AND option rather than a column per option: the option count is
 * the instrument's, not this table's, and a nine-column grid that grows with the
 * ballot is a table that scrolls out of readability on the first crowded race.
 */
function TrendTable({ trend, series }: {
  trend: TrackerTrendDto
  series: { optionId: string; label: string; code: string }[]
}) {
  return (
    <ErpTable head={(
      <>
        <Th>Wave</Th>
        <Th>Poll</Th>
        <Th className="text-right">Version</Th>
        <Th className="text-right">Base n</Th>
        <Th className="text-right">Effective n</Th>
        <Th>Option</Th>
        <Th className="text-right">Estimate</Th>
        <Th>Change on the previous wave</Th>
      </>
    )}>
      {trend.waves.map((wave) => {
        const rows = series
          .map((s) => ({ s, point: wave.points.find((p) => p.optionId === s.optionId) ?? null }))
          .filter((r) => r.point !== null)

        if (rows.length === 0) {
          return (
            <tr key={wave.waveId} className="hover:bg-muted/40">
              <Td className="font-medium">{waveName(wave)}</Td>
              <Td className="font-mono text-erp-xs text-chocolate">{wave.surveyRef ?? '—'}</Td>
              <Td className="text-right tabular-nums text-muted-foreground">
                {wave.versionNo === null ? '—' : `v${wave.versionNo}`}
              </Td>
              {/*
                A raw `<td>` because `Td` takes no colSpan, and the sentence is the
                row — a wave with nothing in it is still a row, never a wave left
                out of the table.
              */}
              <td colSpan={5} className="erp-grid-cell text-muted-foreground">
                {wave.suppressedSentence
                  ?? 'No estimate is published for this wave and no reason was given.'}
              </td>
            </tr>
          )
        }

        return (
          <Fragment key={wave.waveId}>
            {rows.map(({ s, point }, i) => (
              <tr key={`${wave.waveId}-${s.optionId}`} className="hover:bg-muted/40">
                {/* The wave's own figures print once per group. Repeating them on
                    every option row would read as four separate bases. */}
                <Td className="font-medium">{i === 0 ? waveName(wave) : ''}</Td>
                <Td className="font-mono text-erp-xs text-chocolate">
                  {i === 0 ? (wave.surveyRef ?? '—') : ''}
                </Td>
                <Td className="text-right tabular-nums text-muted-foreground">
                  {i === 0 ? (wave.versionNo === null ? '—' : `v${wave.versionNo}`) : ''}
                </Td>
                <Td className="text-right tabular-nums">{count(point!.baseN)}</Td>
                <Td className="text-right tabular-nums text-muted-foreground">
                  {countish(point!.effectiveN)}
                </Td>
                <Td className="truncate" title={s.label}>{s.label || s.code}</Td>
                <Td className="text-right tabular-nums whitespace-nowrap">
                  {pct(point!.estimate)}
                  <span className="ml-1.5 text-erp-xs text-muted-foreground">
                    {pp(point!.moe95)}
                  </span>
                </Td>
                <Td className="whitespace-nowrap">
                  <DeltaCell trend={trend} wave={wave} point={point!} />
                </Td>
              </tr>
            ))}
            {/*
              A wave that published SOME options and withheld others still carries
              its sentence. It is the same fact the chart's dashed column shows.
            */}
            {wave.suppressedSentence && (
              <tr>
                <td colSpan={8} className="erp-grid-cell text-erp-xs text-muted-foreground">
                  {wave.suppressedSentence}
                </td>
              </tr>
            )}
          </Fragment>
        )
      })}
    </ErpTable>
  )
}

/**
 * The change, or the reason there is not one.
 *
 * The reason has three shapes and they are not interchangeable. "Nothing before
 * it" is the first wave and says nothing about the instrument. "No estimate at
 * this wave" is suppression at one end — the other end may be perfectly
 * comparable. "Withheld" is the server refusing the subtraction, and the sentence
 * that explains it is the server's own `comparabilityNote`, which is why this
 * cell points at it rather than paraphrasing it: a per-cell paraphrase of a
 * server-composed sentence is a second copy that drifts.
 */
function DeltaCell({ trend, wave, point }: {
  trend: TrackerTrendDto
  wave: TrackerTrendWaveDto
  point: TrackerTrendPointDto
}) {
  if (point.deltaPp !== null) {
    const against = trend.waves.find((w) => w.waveId === point.comparedToWaveId)
    return (
      <span className="tabular-nums">
        {deltaPp(point.deltaPp)}
        <span className="ml-1.5 text-erp-xs text-muted-foreground">
          {against ? `on ${waveName(against)}` : 'on the previous comparable wave'}
        </span>
      </span>
    )
  }

  if (asNumber(point.estimate) === null) {
    return <span className="text-erp-xs text-muted-foreground">
      No estimate at this wave, so there is no end to subtract from.
    </span>
  }

  // The first wave that carries this option. Not "wave 1": an option added at
  // wave 3 has nothing before it either, and saying so is the honest reason.
  const earlier = trend.waves
    .slice(0, trend.waves.findIndex((w) => w.waveId === wave.waveId))
    .some((w) => w.points.some((p) => p.optionId === point.optionId
      && asNumber(p.estimate) !== null))

  if (!earlier) {
    return <span className="text-erp-xs text-muted-foreground">
      Nothing before it to compare against.
    </span>
  }

  /*
    A null delta with an estimate at both ends means the server declined the
    subtraction — but only when it composed a sentence saying why. Without one,
    the DTO's other cause applies: the immediately previous COMPARABLE wave
    suppressed this option, so there is nothing adjacent to subtract from even
    though an older wave carries a figure. Printing "Withheld — identical
    instrument." there invents a verdict the server never reached, and pointing
    at a note that is not on the page sends the reader looking for nothing.
  */
  if (!trend.comparabilityNote) {
    return <span className="text-erp-xs text-muted-foreground">
      No comparable previous estimate for this option.
    </span>
  }

  return (
    <span className="text-erp-xs text-amber-700 dark:text-amber-200">
      Withheld — {comparabilityLabel(trend.comparability).toLowerCase()}. The reason is stated in
      full above the chart.
    </span>
  )
}
